import { Injectable, inject, effect } from '@angular/core';
import { environment } from '@brighthub/environment';
import * as amplitude from '@amplitude/analytics-browser';
import { AuthService } from '@brightside/core/auth';
import { Identify } from '@amplitude/analytics-browser';

@Injectable({
  providedIn: 'root',
})
export class AmplitudeService {
  private _initialized = false;
  private _authService = inject(AuthService);

  constructor() {
    this._init();
    effect(() => {
      const _user = this._authService.user();

      if (_user) {
        this.setUserId(_user.sub);
        this.setUserProperties({
          email: _user.email,
          name: _user.name,
        });
      } else {
        this.reset();
      }
    });
  }

  private _init(): void {

    if (this._initialized) return;

    const apiKey = environment.amplitude.apiKey;
    if (!apiKey) {
      console.warn('Amplitude API key is missing in environment.');
      return;
    }

    amplitude.init(apiKey, {
      autocapture: {
        attribution: true,
        pageViews: false,
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
      },
      appVersion: environment.appVersion,
    });

    this._initialized = true;
  }

  logEvent(eventName: string, eventProperties?: Record<string, any>): void {

    if (!environment.production) {
      console.log(
        '[AmplitudeService] logEvent',
        eventName,
        eventProperties ? Object.keys(eventProperties) : []
      );
    }

    if (!this._initialized) {
      console.warn(`Amplitude not initialized, skipping event: ${eventName}`);
      return;
    }
    const transformedName = eventName.replace(/_/g, ' ');
    amplitude.track(transformedName, eventProperties ?? {});
  }

  setUserId(userId: string): void {
    amplitude.setUserId(userId);
  }

  setUserProperties(properties: Record<string, any>): void {
    const identify = new Identify();
    for (const [key, value] of Object.entries(properties)) {
      identify.set(key, value);
    }
    amplitude.identify(identify);
  }

  reset(): void {
    amplitude.reset();
  }
}
